import type { RecipeListItem } from '../../shared/types/lists';
/**
 * Internal dependencies
 */
export type RemoveItemsFromUserListParams = {
	listId: string;
	item: RecipeListItem;
};

/**
 * Remove items from a user's list
 * @link https://evermade.stoplight.io/docs/valio-app/zx747dzltyq81-delete-users-user-id-lists-list-id-items
 * @param {AddItemsToUserListParams} params - The parameters for removing items from the list.
 * @param {string} params.listId - The ID of the list to remove items from.
 * @param {RecipeListItem} params.item - The item to remove from the list.
 * @param {string} params.item.id - The ID of the item to remove.
 * @return {Promise<RecipeListItem>} A promise that resolves to the updated user's list items.
 */
export async function removeItemsFromUserList(
	params: RemoveItemsFromUserListParams
): Promise<RecipeListItem> {
	const { host } = window.valioConfig;
	const { session } = window.valioApp;

	// Validate required params
	if (!params.item || !params.item.id) {
		throw new Error('Item ID is required to remove from a user list.');
	}

	if (!params.listId) {
		throw new Error('List ID is required to remove items from a user list.');
	}

	// Ensure the session is initialized
	if (!session.initialized) {
		await session.init();
	}

	// Ensure the user is authenticated
	if (!session.userId) {
		return Promise.reject(new Error('User is not authenticated. Cannot remove items from list.'));
	}

	const headers = await session.authHeader();

	// Make the API request to create the list
	return fetch(
		`${host}/api/v1/users/${session.userId}/lists/${params.listId}/items/${params.item.id}`,
		{
			method: 'DELETE',
			headers,
		}
	)
		.then(async (response) => {
			if (!response.ok) {
				// Handle HTTP errors like 401, 404, 500, etc.
				const errorText = await response.text();
				throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
			}

			// Pass the full item back since the API does not return it
			return params.item;
		})
		.catch((error) => {
			console.error('Error creating user list:', error);
			throw error;
		});
}
